import { gql, useQuery } from "@apollo/client";
import { AppContext } from "@edx/frontend-platform/react";
import { useContext } from "react";

const BASIC_COURSE_DETAILS = gql`
  fragment BasicCourseDetails on Courses {
    id
    price
    openedxCourseId
    courseTags {
      tag
    }
  }
`;

const OPENEDX_COURSE_DETAILS = gql`
  fragment OpenedxCourseDetails on CourseOverviewsCourseoverview {
    id
    displayName
    displayNumberWithDefault
    courseImageUrl
    effort
    shortDescription
  }
`;

const PROGRAM_DETAILS = gql`
  fragment ProgramDetails on Programs {
    about
    achievements
    description
    duration
    durationMetric
    inviteOnly
    name
    id
    organisationId
    price
    published
    programCoursesAggregate {
      agg_count
    }
    image
    programTags {
      tag
      count
    }
}
`;

const GET_DISCOVER_PROGRAMS = gql`
  query GetDiscoverPrograms($userId: Int4!) {
    discoverPrograms: programs(where: {published: {_eq: "true"}, _not: {programEnrollments: {userId: {_eq: $userId}}}}) {
      ...ProgramDetails
      programCourses {
        course {
          ...BasicCourseDetails
          details: _CourseOverviewsCourseoverview {
            ...OpenedxCourseDetails
          }
        }
      }
    }
  }
  ${PROGRAM_DETAILS}
  ${BASIC_COURSE_DETAILS}
  ${OPENEDX_COURSE_DETAILS}
`;

const GET_ENROLLED_PROGRAMS = gql`
  query GetEnrolledPrograms {
    enrolledPrograms: programEnrollments {
      program {
        ...ProgramDetails
        programCourses {
          course {
            ...BasicCourseDetails
            details: _CourseOverviewsCourseoverview {
              ...OpenedxCourseDetails
            }
          }
        }
      }
    }
  }
  ${PROGRAM_DETAILS}
  ${BASIC_COURSE_DETAILS}
  ${OPENEDX_COURSE_DETAILS}
`;

const GET_PROGRAM_BY_ID = gql`
  query GetProgramById($programId: Uuid) {
    programById: programs(where: {id: {_eq: $programId}}) {
      ...ProgramDetails
      programCourses {
        course {
          ...BasicCourseDetails
          details: _CourseOverviewsCourseoverview {
            ...OpenedxCourseDetails
          }
        }
      }
    }
  }
  ${PROGRAM_DETAILS}
  ${BASIC_COURSE_DETAILS}
  ${OPENEDX_COURSE_DETAILS}
`;

export const useDiscoverPrograms = () => {
  const { authenticatedUser } = useContext(AppContext);

  const { data, loading, error } = useQuery(GET_DISCOVER_PROGRAMS, {
    variables: { userId: authenticatedUser?.id },
    context: { headers: { "x-hasura-role": "learner" } },
    skip: !authenticatedUser?.id
  });

  return {
    discoverPrograms: data?.discoverPrograms || [],
    loading,
    error
  };
};

export const useEnrolledPrograms = () => {
  const { data, loading, error } = useQuery(GET_ENROLLED_PROGRAMS, {
    context: { headers: { "x-hasura-role": "learner" } }
  });

  return {
    enrolledPrograms: data?.enrolledPrograms || [],
    loading,
    error
  };
};

export const useProgramById = (programId) => {
  const { data, loading, error } = useQuery(GET_PROGRAM_BY_ID, {
    variables: { programId },
    context: { headers: { "x-hasura-role": "learner" } },
    skip: !programId
  });

  return {
    program: data?.programById?.[0] || null,
    loading,
    error
  };
};
